/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import CustomerHeader from '../components/CustomerHeader';
import './MyServiceBookingsPage.css';

interface ServiceBooking {
  id: string;
  bookingId?: string;
  providerId: string;
  providerName?: string;
  provider?: any;
  category?: string;
  date: string;
  time: string;
  address: string;
  description?: string;
  status: 'pending' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled';
  createdAt?: string;
  price?: string;
}

const MyServiceBookingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'en';
  });
  const [bookings, setBookings] = useState<ServiceBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled'>('all');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/services/my-bookings' } });
      return;
    }

    loadBookings();
  }, [isAuthenticated, navigate]);

  const loadBookings = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setError(null);
      const bookingsData = await apiService.getUserServiceBookings(user.id);
      setBookings(bookingsData || []);
    } catch (err: any) {
      console.error('Error loading bookings:', err);
      setError(err.message || 'Failed to load bookings');
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewProvider = (providerId: string) => {
    navigate(`/services/provider/${providerId}`);
  };

  const handleCancelBooking = async (bookingId: string) => {
    if (!window.confirm(language === 'hi' 
      ? 'क्या आप इस बुकिंग को रद्द करना चाहते हैं?' 
      : 'Are you sure you want to cancel this booking?')) {
      return;
    }

    try {
      await apiService.cancelServiceBooking(bookingId);
      // Reload bookings
      loadBookings();
    } catch (err: any) {
      console.error('Error cancelling booking:', err);
      alert(err.message || 'Failed to cancel booking');
    }
  };

  const formatDate = (dateStr: string) => {
    if (dateStr === 'today') {
      return new Date().toLocaleDateString(language === 'hi' ? 'hi-IN' : 'en-IN', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } else if (dateStr === 'tomorrow') {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow.toLocaleDateString(language === 'hi' ? 'hi-IN' : 'en-IN', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
    try {
      return new Date(dateStr).toLocaleDateString(language === 'hi' ? 'hi-IN' : 'en-IN', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return '#f59e0b';
      case 'confirmed': return '#10b981';
      case 'in_progress': return '#3b82f6';
      case 'completed': return '#10b981';
      case 'cancelled': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, { en: string; hi: string }> = {
      pending: { en: 'Pending', hi: 'लंबित' },
      confirmed: { en: 'Confirmed', hi: 'पुष्टि की गई' },
      in_progress: { en: 'In Progress', hi: 'प्रगति में' },
      completed: { en: 'Completed', hi: 'पूर्ण' },
      cancelled: { en: 'Cancelled', hi: 'रद्द' }
    };
    const statusLabels = labels[status];
    if (!statusLabels) return status;
    const lang = language as 'en' | 'hi';
    return statusLabels[lang] || status;
  };

  const filteredBookings = filter === 'all' 
    ? bookings 
    : bookings.filter(b => b.status === filter);

  if (loading) {
    return (
      <>
        <CustomerHeader
          variant="sticky"
          showTagline
          showVoiceSearch
          showProfileDropdown
          showBackButton
          onBackClick={() => navigate('/services')}
          onSearch={(query) => {
            navigate(`/search?q=${encodeURIComponent(query)}`);
          }}
          onCartClick={() => navigate('/cart')}
        />
        <div className="my-service-bookings-page">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>{language === 'hi' ? 'लोड हो रहा है...' : 'Loading bookings...'}</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <CustomerHeader
        variant="sticky"
        showTagline
        showVoiceSearch
        showProfileDropdown
        showBackButton
        onBackClick={() => navigate('/services')}
        onSearch={(query) => {
          navigate(`/search?q=${encodeURIComponent(query)}`);
        }}
        onCartClick={() => navigate('/cart')}
      />
      <div className="my-service-bookings-page">
        <main className="bookings-main">
          {/* Header */}
          <section className="bookings-header">
            <h1 className="bookings-title">
              {language === 'hi' ? 'मेरी बुकिंग' : 'My Bookings'}
            </h1>
            <p className="bookings-subtitle">
              {language === 'hi' 
                ? `${bookings.length} ${bookings.length === 1 ? 'बुकिंग' : 'बुकिंग'} मिली` 
                : `${bookings.length} ${bookings.length === 1 ? 'booking' : 'bookings'} found`}
            </p>
          </section>

          {/* Filter Tabs */}
          <section className="filter-tabs">
            {(['all', 'pending', 'confirmed', 'in_progress', 'completed', 'cancelled'] as const).map((status) => (
              <button
                key={status}
                className={`filter-tab ${filter === status ? 'active' : ''}`}
                onClick={() => setFilter(status)}
              >
                {status === 'all' 
                  ? (language === 'hi' ? 'सभी' : 'All')
                  : getStatusLabel(status)}
              </button>
            ))}
          </section>

          {/* Error State */}
          {error && (
            <div className="error-banner">
              <span className="error-icon">⚠️</span>
              <span className="error-text">{error}</span>
              <button onClick={loadBookings} className="retry-btn">
                {language === 'hi' ? 'पुनः प्रयास करें' : 'Retry'}
              </button>
            </div>
          )}

          {/* Bookings List */}
          {filteredBookings.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <h3>
                {language === 'hi' ? 'कोई बुकिंग नहीं मिली' : 'No Bookings Found'}
              </h3>
              <p>
                {filter === 'all'
                  ? (language === 'hi' 
                      ? 'आपकी कोई सेवा बुकिंग नहीं है' 
                      : 'You don\'t have any service bookings')
                  : (language === 'hi'
                      ? `कोई ${getStatusLabel(filter)} बुकिंग नहीं मिली`
                      : `No ${getStatusLabel(filter)} bookings found`)}
              </p>
              <button
                className="browse-services-btn"
                onClick={() => navigate('/services')}
              >
                {language === 'hi' ? 'सेवाएं ब्राउज़ करें' : 'Browse Services'}
              </button>
            </div>
          ) : (
            <div className="bookings-list">
              {filteredBookings.map((booking) => (
                <article key={booking.id || booking.bookingId} className="booking-card">
                  <div className="booking-card-header">
                    <div className="booking-id-section">
                      <span className="booking-id-label">
                        {language === 'hi' ? 'बुकिंग आईडी' : 'Booking ID'}:
                      </span>
                      <span className="booking-id-value">
                        {booking.id || booking.bookingId || 'N/A'}
                      </span>
                    </div>
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(booking.status) + '20', color: getStatusColor(booking.status) }}
                    >
                      {getStatusLabel(booking.status)}
                    </span>
                  </div>

                  <div className="booking-details">
                    {booking.providerName && (
                      <div className="detail-row">
                        <span className="detail-label">
                          {language === 'hi' ? 'सेवा प्रदाता' : 'Service Provider'}:
                        </span>
                        <span className="detail-value">{booking.providerName}</span>
                      </div>
                    )}

                    {booking.category && (
                      <div className="detail-row">
                        <span className="detail-label">
                          {language === 'hi' ? 'श्रेणी' : 'Category'}:
                        </span>
                        <span className="detail-value">{booking.category}</span>
                      </div>
                    )}

                    <div className="detail-row">
                      <span className="detail-label">
                        {language === 'hi' ? 'तारीख' : 'Date'}:
                      </span>
                      <span className="detail-value">{formatDate(booking.date)}</span>
                    </div>

                    <div className="detail-row">
                      <span className="detail-label">
                        {language === 'hi' ? 'समय' : 'Time'}:
                      </span>
                      <span className="detail-value">{booking.time}</span>
                    </div>

                    <div className="detail-row">
                      <span className="detail-label">
                        {language === 'hi' ? 'पता' : 'Address'}:
                      </span>
                      <span className="detail-value">{booking.address}</span>
                    </div>

                    {booking.description && (
                      <div className="detail-row">
                        <span className="detail-label">
                          {language === 'hi' ? 'विवरण' : 'Description'}:
                        </span>
                        <span className="detail-value">{booking.description}</span>
                      </div>
                    )}

                    {booking.price && (
                      <div className="detail-row">
                        <span className="detail-label">
                          {language === 'hi' ? 'कीमत' : 'Price'}:
                        </span>
                        <span className="detail-value price">{booking.price}</span>
                      </div>
                    )}
                  </div>

                  <div className="booking-actions">
                    {booking.providerId && (
                      <button
                        className="action-btn view-provider-btn"
                        onClick={() => handleViewProvider(booking.providerId)}
                      >
                        👤 {language === 'hi' ? 'प्रदाता देखें' : 'View Provider'}
                      </button>
                    )}
                    {(booking.status === 'pending' || booking.status === 'confirmed') && (
                      <button
                        className="action-btn cancel-btn"
                        onClick={() => handleCancelBooking(booking.id || booking.bookingId || '')}
                      >
                        ❌ {language === 'hi' ? 'रद्द करें' : 'Cancel'}
                      </button>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default MyServiceBookingsPage;
